import type {
  JourneyPlanRequest,
  LatLngString,
  TransportMode,
} from '../arcticapi';

type Coordinate = [number, number];

export type JourneyPlanTimeMode = 'now' | 'leaveAt' | 'arriveBy';
export type JourneyPlanWalkingSpeed = 'slow' | 'medium' | 'fast';
export type JourneyPlanTravelOption =
  | 'bus'
  | 'ferry'
  | 'train'
  | 'coach'
  | 'tram'
  | 'tube';

export interface JourneyPlanTimeSelection {
  mode: JourneyPlanTimeMode;
  date: string;
  time: string;
}

export interface JourneyPlanAdvancedFilters {
  travel: JourneyPlanTravelOption[];
  minimalWalking: boolean;
  walkingSpeed: JourneyPlanWalkingSpeed;
}

export interface JourneyPlanLocation {
  coordinate: Coordinate;
  name: string;
}

export interface JourneyPlanSearchRequest {
  origin: JourneyPlanLocation;
  destination: JourneyPlanLocation;
  time: JourneyPlanTimeSelection;
  filters: JourneyPlanAdvancedFilters;
}

// Map localised UI names to API transport modes
const transportModeMap: Record<JourneyPlanTravelOption, TransportMode> = {
  bus: 'bus',
  ferry: 'ferry',
  train: 'rail',
  coach: 'coach',
  tram: 'tram',
  tube: 'subway',
};

export function createDefaultJourneyPlanTimeSelection(
  now = new Date(),
): JourneyPlanTimeSelection {
  return {
    mode: 'now',
    date: formatLocalDate(now),
    time: formatLocalTime(now),
  };
}

export function createDefaultJourneyPlanAdvancedFilters(): JourneyPlanAdvancedFilters {
  return {
    travel: [],
    minimalWalking: false,
    walkingSpeed: 'medium',
  };
}

export function buildJourneyPlanApiRequest(
  request: JourneyPlanSearchRequest,
): JourneyPlanRequest {
  const apiRequest: JourneyPlanRequest = {
    origin: coordinateToLatLngString(request.origin.coordinate),
    destination: coordinateToLatLngString(request.destination.coordinate),
    originName: request.origin.name,
    destinationName: request.destination.name,
  };
  const requestedDateTime = buildRequestedDateTime(request.time);

  if (request.time.mode === 'leaveAt' && requestedDateTime) {
    apiRequest.leaveAfter = requestedDateTime;
  }

  if (request.time.mode === 'arriveBy' && requestedDateTime) {
    apiRequest.arriveBy = requestedDateTime;
  }

  const options: NonNullable<JourneyPlanRequest['options']> = {};

  if (request.filters.travel.length > 0) {
    options.mode = request.filters.travel.map(
      (transport) => transportModeMap[transport],
    );
  }

  if (request.filters.minimalWalking) {
    options.minimiseWalking = true;
  }

  if (request.filters.walkingSpeed !== 'medium') {
    options.walkSpeed = request.filters.walkingSpeed;
  }

  if (Object.keys(options).length > 0) {
    apiRequest.options = options;
  }

  return apiRequest;
}

function coordinateToLatLngString(coordinate: Coordinate): LatLngString {
  const [lng, lat] = coordinate;

  return `${lat},${lng}`;
}

export function formatJourneySearchTimingLabel(
  time: JourneyPlanTimeSelection,
): string {
  if (time.mode === 'now') {
    return 'Leaving now';
  }

  const date = new Date(`${time.date}T${time.time}:00`);
  const dateLabel = Number.isNaN(date.getTime())
    ? `${time.date} ${time.time}`
    : new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);

  return `${time.mode === 'arriveBy' ? 'Arriving by' : 'Leaving at'} ${dateLabel}`;
}

function buildRequestedDateTime(
  time: JourneyPlanTimeSelection,
): string | undefined {
  if (time.mode === 'now') {
    return undefined;
  }

  if (!time.date || !time.time) {
    return undefined;
  }

  return `${time.date}T${time.time}:00`;
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function formatLocalTime(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}
